/* eslint-disable */
/**
 * ═════════════════════════════════════════════════════════════════════════
 *   Cocoon Nest — BillingEngine (IMMUTABLE)
 *   Iter-301 · Feb 2026 · "Break-the-Glass" isolation of the dynamic
 *   surcharge + tax + invoice math.
 * ═════════════════════════════════════════════════════════════════════════
 *
 *   THIS FILE IS IMMUTABLE.
 *
 *   Master rule (also recorded in SYSTEM_RULES.md):
 *     "Dynamic Billing: All tax and surcharge math must be fetched from
 *      branch settings and processed only by BillingEngine.jsx."
 *
 *   Contract:
 *     1. Every surcharge / tax / price-mode calculation in the app
 *        (POS Order page, Item rows, Sales Reports, Legacy Reports,
 *        Finance Summary, receipts, exports) MUST route through the
 *        helpers exported from this file. Inlining a rate anywhere else
 *        is a P0 regression and MUST be reverted.
 *     2. HARDCODING FORBIDDEN. The engine NEVER assumes 10 / 11 / any
 *        specific rate. Every rate is fetched from either
 *          (a) the persisted order's `breakdown.surcharges` /
 *              `breakdown.taxes`, or
 *          (b) the branch's live `surcharge_types` collection filtered
 *              by `active: True`.
 *        A branch with zero surcharge types produces zero rate. A branch
 *        that later flips PPN from 11% → 12% is picked up on the next
 *        recalc without a code change.
 *     3. Nett vs Plus is authoritative on the branch (`branch.price_mode`).
 *        In "nett" mode prices are inclusive → base is back-solved.
 *        In "plus_tax" mode the invoice adds each surcharge + tax on top.
 *     4. Live state refresh: every recalc uses the ORDER's own breakdown
 *        when available (so historical orders stay stable at their
 *        posted-rate) and falls back to the BRANCH's live config for
 *        Sales / Finance reports (which price against the current rate
 *        card). Consumers MUST NOT cache rates across renders.
 *     5. The engine is pure JavaScript. No React state, no I/O, no DOM.
 *        Callers fetch data and pass it in; the engine returns numbers.
 *     6. Global scope: applies to all 8 branches (Ubud, Canggu, Seminyak,
 *        …) — one engine, one behaviour, forever.
 *
 *   ── Where the logic was extracted from ────────────────────────────
 *     - `OrderItemsCard.jsx` (sRate/tRate summation from breakdown).
 *     - `ItemRow.jsx` (per-line nett-mode decomposition — lineBase /
 *       lineSurcharge / linePpn).
 *     - `OrdersLegacyReport.jsx` (used to hardcode "10%" / "11%" in
 *       column headers — now driven by backend `service_charge_rate_pct`
 *       + `tax_rate_pct` piped through this engine).
 *     - `FinanceSummaryReport.jsx` (row labels for Taxes / Service
 *       Charges — same dynamic pipe).
 *   ─────────────────────────────────────────────────────────────────────
 */

// ── Rate extraction ─────────────────────────────────────────────────
// The persisted `order.breakdown.surcharges` / `.taxes` arrays are the
// authoritative snapshot for THIS invoice. We sum whatever the backend
// wrote — no assumption on how many surcharges / taxes a branch runs.

export function extractSurchargeRate(breakdown) {
  if (!breakdown || !Array.isArray(breakdown.surcharges)) return 0;
  const pct = breakdown.surcharges.reduce((s, t) => s + (Number(t.rate) || 0), 0);
  return pct / 100;
}

export function extractTaxRate(breakdown) {
  if (!breakdown || !Array.isArray(breakdown.taxes)) return 0;
  const pct = breakdown.taxes.reduce((s, t) => s + (Number(t.rate) || 0), 0);
  return pct / 100;
}

export function extractPriceMode(breakdown, branch) {
  return (
    (breakdown && breakdown.price_mode) ||
    (branch && branch.price_mode) ||
    "plus_tax"
  );
}

export function extractRatesFromBreakdown(breakdown, branch = null) {
  return {
    priceMode: extractPriceMode(breakdown, branch),
    surchargeRate: extractSurchargeRate(breakdown),
    taxRate: extractTaxRate(breakdown),
  };
}

// Fallback path for report screens that don't own a specific order —
// they aggregate across a date window, so the rate comes from the live
// branch surcharge_types config. The report backend already exposes
// `service_charge_rate_pct` and `tax_rate_pct` for that reason.

export function extractRatesFromReport(reportData) {
  return {
    surchargeRatePct: Number(reportData?.service_charge_rate_pct ?? 0),
    taxRatePct: Number(reportData?.tax_rate_pct ?? 0),
  };
}

// ── Rate label helpers ──────────────────────────────────────────────
// Never hardcode the number in a template literal — always route
// through `rateLabel` so the % char + rounding stay uniform.

export function rateLabel(ratePct) {
  const n = Number(ratePct);
  if (!Number.isFinite(n) || n === 0) return "";
  return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, "")}%`;
}

export function surchargeColumnLabel(ratePct, prefix = "Service Charge") {
  const label = rateLabel(ratePct);
  return label ? `${prefix} (${label})` : prefix;
}

export function taxColumnLabel(ratePct, prefix = "PPN") {
  const label = rateLabel(ratePct);
  return label ? `${prefix} (${label})` : prefix;
}

// ── Per-line decomposition ──────────────────────────────────────────
// Given a line's discounted final total (post-adjustment, post-loyalty)
// return its base / surcharge / tax split. In "plus_tax" mode the input
// IS the base; in "nett" mode we reverse the inclusive price. Gift-card
// and commission-only rows carry NO surcharge or tax (contract with
// backend `orders_calc.py::_compute_breakdown`).

export function decomposeLineFinal({
  discountedFinal,
  priceMode = "plus_tax",
  surchargeRate = 0,
  taxRate = 0,
  taxable = true,
}) {
  const final = Math.max(0, Number(discountedFinal) || 0);
  if (!taxable) {
    return { base: final, surcharge: 0, tax: 0 };
  }
  if (priceMode === "nett") {
    const divisor = (1 + surchargeRate) * (1 + taxRate);
    const base = divisor > 0 ? final / divisor : final;
    const surcharge = base * surchargeRate;
    const tax = (base + surcharge) * taxRate;
    return { base, surcharge, tax };
  }
  // plus_tax — the passed `discountedFinal` is already the base;
  // surcharge + tax are additive on top and NOT part of the input.
  const base = final;
  const surcharge = base * surchargeRate;
  const tax = (base + surcharge) * taxRate;
  return { base, surcharge, tax };
}

// ── Invoice-level breakdown rendering ───────────────────────────────
// Emits the ordered list of rows the Order-Summary card renders under
// "Base price". Each entry mirrors the backend snapshot exactly so we
// never invent a row that isn't in `order.breakdown`.

export function summarizeBreakdown(breakdown) {
  const rows = [];
  const surcharges = (breakdown && breakdown.surcharges) || [];
  const taxes = (breakdown && breakdown.taxes) || [];
  for (const s of surcharges) {
    rows.push({
      kind: "surcharge",
      id: s.id || s.name,
      name: s.name,
      rate: Number(s.rate) || 0,
      amount: Number(s.amount) || 0,
    });
  }
  for (const t of taxes) {
    rows.push({
      kind: "tax",
      id: t.id || t.name,
      name: t.name,
      rate: Number(t.rate) || 0,
      amount: Number(t.amount) || 0,
    });
  }
  return rows;
}

// ── Gift-card + commission-only carve-out ───────────────────────────
// Callers ask the engine whether a line contributes to surcharge / tax
// so the per-line decomposition guard stays in one place. Gift-card
// sales and Package commission-only sub-rows are EXCLUDED per the
// backend rule.

export function lineIsTaxable(item) {
  if (!item) return false;
  if (item.kind === "gift_card") return false;
  if (item.commission_only) return false;
  return true;
}

// ── Grand-total sanity ──────────────────────────────────────────────
// The Order page reads `order.total` as the authoritative grand-total
// (server-authored). Exposed here so any future consumer that composes
// its own preview total routes through the same helper.

export function orderGrandTotal(order) {
  if (!order) return 0;
  return Number(order.total) || 0;
}

export function orderBalanceDue(order) {
  if (!order) return 0;
  const total = orderGrandTotal(order);
  const paid = Number(order.amount_paid || 0);
  return order.balance_due != null ? Number(order.balance_due) : Math.max(0, total - paid);
}
